// =============================================================================
// Admin Analytics — faceted drilldown filters
// =============================================================================
// A chip/token filter bar (the Linear / GitHub / Notion pattern) for the raw
// data drilldowns. Instead of a wall of always-open inputs:
//   - an "Add filter" button opens a field picker, then a searchable multi-select
//     of the distinct values actually present in the data;
//   - each active filter renders as a removable chip summarizing its selections;
//   - values within one field are OR'd, different fields are AND'd — so you can
//     filter for several people at once, then narrow by status, etc.
// Every field is faceted (multi-select of present values); `kind` on the field
// config is retained for back-compat but no longer changes behavior.
//
// Options come from one of two places: computed from `data` via each field's
// `get` (client-side drilldowns), or handed in as `options` (server-side
// drilldowns, where the backend reports each field's values + counts and the
// selection is sent back as query filters instead of applied here). A server
// drilldown can also hand a field's search to the backend (`onSearchChange`),
// for fields with more values than it lists at once (`truncatedKeys`).
//
// Date filter: an optional date-range filter lives alongside the faceted fields.
// Pass `dateValue` + `onDateChange` to enable it. "Date" appears in the Add
// filter menu when no date is active; when active it renders as a chip with a
// portaled CalendarPicker (bypasses the Dialog's overflow:hidden).
// =============================================================================
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { CalendarDateRange, CalendarPicker } from "@fronterahealth/frontera-ui-components";

// Adds a class to <body> while mounted and removes it on unmount.
// Used to raise Popper.js z-index when a portaled CalendarPicker is open —
// see body.date-chip-open rule in AdminAnalytics.tsx MotionStyles.
const BodyClass: React.FC<{ name: string }> = ({ name }) => {
  useEffect(() => {
    document.body.classList.add(name);
    return () => document.body.classList.remove(name);
  }, [name]);
  return null;
};

/** A filter chip's identity: the key its selections are stored under, and its label. */
export interface FacetField {
  key: string;
  label: string;
}

/** One selectable value. `label` defaults to `value`; `count` is shown when present. */
export interface FacetOption {
  value: string;
  label?: string;
  count?: number;
}

/**
 * One filterable/exportable field. `get` yields the string used for matching,
 * the chip/option label, and the CSV cell — one source of truth per column.
 */
export interface DrilldownFilterField<T> extends FacetField {
  get: (row: T) => string;
  /** @deprecated retained for back-compat; every field is now a faceted multi-select. */
  kind?: "text" | "select";
}

/** fieldKey → selected values. A field with a non-empty array is "active". */
export type FilterValues = Record<string, string[]>;

/** Apply the faceted selection: OR within a field, AND across fields. */
export const applyDrilldownFilters = <T,>(data: T[], fields: DrilldownFilterField<T>[], values: FilterValues): T[] => {
  const active = fields.filter((f) => (values[f.key]?.length ?? 0) > 0);
  if (!active.length) return data;
  return active.reduce((rows, f) => rows.filter((row) => values[f.key].includes(f.get(row))), data);
};

// ----- Small click-outside popover -------------------------------------------

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "left" | "right";
}

const Popover: React.FC<PopoverProps> = ({ open, onClose, trigger, children, align = "left" }) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, right: 0 });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Compute dropdown position from the trigger's viewport rect each time the popover opens.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, right: window.innerWidth - r.right });
  }, [open]);

  return (
    <div ref={triggerRef}>
      {trigger}
      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[200]" onClick={onClose} aria-hidden />
            <div
              className="fixed z-[201] w-64 origin-top rounded-lg border border-limestone-100 bg-white p-2 shadow-lg"
              style={{
                top: pos.top,
                ...(align === "right" ? { right: pos.right } : { left: pos.left }),
                animation: "aa-rise 140ms ease-out",
              }}
            >
              {children}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

// ----- Value multi-select (searchable checkbox list) -------------------------

interface ValueListProps {
  options: FacetOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  /** Also search on the server; the options narrow here at once and reload as it answers. */
  onSearchChange?: (query: string) => void;
  /** Shown under the search box, e.g. that the list is cut short. */
  hint?: string;
}

const ValueList: React.FC<ValueListProps> = ({ options, selected, onToggle, onClear, onSearchChange, hint }) => {
  const [search, setSearch] = useState("");
  const shown = useMemo(
    () => (search ? options.filter((o) => (o.label ?? o.value).toLowerCase().includes(search.toLowerCase())) : options),
    [options, search],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5 rounded-md border border-limestone-200 px-2">
        <MagnifyingGlassIcon className="h-3.5 w-3.5 text-text-secondary" />
        <input
          autoFocus
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onSearchChange?.(e.target.value);
          }}
          placeholder="Search values"
          className="h-7 w-full appearance-none border-0 bg-transparent text-xs text-text-primary shadow-none placeholder:text-text-secondary focus:outline-none focus:ring-0"
        />
      </div>
      {hint && <p className="px-2 text-[11px] text-text-secondary">{hint}</p>}
      <div className="max-h-56 overflow-y-auto">
        {shown.length === 0 ? (
          <p className="px-2 py-3 text-center text-xs text-text-secondary">No matches</p>
        ) : (
          shown.map((opt) => {
            const isChecked = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-limestone-50 focus:outline-none"
              >
                <span
                  className={clsx(
                    "grid h-4 w-4 shrink-0 place-items-center rounded border",
                    isChecked ? "border-rio-600 bg-rio-600 text-white" : "border-limestone-300 bg-white",
                  )}
                >
                  {isChecked && <CheckIcon className="h-3 w-3" />}
                </span>
                <span className="flex-1 truncate text-text-primary">{(opt.label ?? opt.value) || "—"}</span>
                {opt.count !== undefined && (
                  <span className="shrink-0 tabular-nums text-text-secondary">{opt.count}</span>
                )}
              </button>
            );
          })
        )}
      </div>
      {selected.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="self-start px-2 text-[11px] text-text-secondary hover:text-primary"
        >
          Clear selection
        </button>
      )}
    </div>
  );
};

// ----- Date chip with portaled CalendarPicker --------------------------------

const formatDateRangeLabel = (range: CalendarDateRange): string => {
  if (range.selectedLabel) return range.selectedLabel;
  if (range.startDate && range.endDate)
    return `${format(range.startDate, "MMM d")} – ${format(range.endDate, "MMM d, yyyy")}`;
  return "Custom range";
};

const DateChip: React.FC<{
  range: CalendarDateRange;
  label: string;
  onChange: (r: CalendarDateRange) => void;
  onClear: () => void;
}> = ({ range, label, onChange, onClear }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const chipRef = useRef<HTMLDivElement>(null);
  // Local state so the CalendarPicker tracks partial selections (start-only)
  // without the external `range` prop resetting its display mid-pick.
  const [pickerRange, setPickerRange] = useState<CalendarDateRange | undefined>(undefined);

  const openPicker = () => {
    setPickerRange(range); // seed with current range
    if (chipRef.current) {
      const r = chipRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, left: r.left });
    }
    setOpen(true);
  };

  return (
    <div ref={chipRef}>
      <div className="flex items-center overflow-hidden rounded-full border border-limestone-300 bg-white text-xs">
        <button
          type="button"
          onClick={openPicker}
          className="flex items-center gap-1 py-1 pl-2.5 pr-1.5 hover:bg-limestone-50"
        >
          <span className="font-medium text-text-secondary">{label}:</span>
          <span className="max-w-[18rem] truncate text-text-primary">{formatDateRangeLabel(range)}</span>
          <ChevronDownIcon className="h-3 w-3 text-text-secondary" />
        </button>
        <button
          type="button"
          aria-label={`Remove ${label} filter`}
          onClick={onClear}
          className="border-l border-limestone-300 px-1.5 py-1 text-text-secondary hover:bg-limestone-50 hover:text-primary"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      {open &&
        createPortal(
          <>
            <BodyClass name="date-chip-open" />
            <div className="fixed inset-0 z-[200]" onClick={() => setOpen(false)} aria-hidden />
            <div
              className="fixed z-[201] rounded-lg border border-limestone-100 bg-white p-3 shadow-lg"
              style={{ top: pos.top, left: pos.left, minWidth: 300, animation: "aa-rise 140ms ease-out" }}
            >
              <CalendarPicker
                placeholder="Date"
                inputSize="full"
                mode="filter"
                selectedDateRange={pickerRange}
                selectedLabel={pickerRange?.selectedLabel}
                onDateChange={(r) => {
                  setPickerRange(r); // track partial selection so picker doesn't reset mid-pick
                  if (r.startDate && r.endDate) {
                    onChange(r);
                    setOpen(false);
                  }
                }}
              />
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

// ----- The bar ----------------------------------------------------------------

interface FacetedFilterBarProps<T> {
  fields: Array<FacetField & { get?: (row: T) => string }>;
  /** Client mode: options are the distinct `get` values across these rows. */
  data?: T[];
  /** Server mode: options per field key, as reported by the backend. Takes precedence over `data`. */
  options?: Record<string, FacetOption[]>;
  /** Display label for a selected value that isn't among the loaded options (e.g. a seed, before options load). */
  valueLabel?: (key: string, value: string) => string | undefined;
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  /**
   * When provided, a "Date" entry appears in the Add filter menu and as an
   * active chip when `dateValue` is set. Completely independent of global filters.
   */
  dateValue?: CalendarDateRange;
  onDateChange?: (r: CalendarDateRange | undefined) => void;
  /** Label shown on the date chip and in the Add filter menu. Defaults to "Date". */
  dateLabel?: string;
  /**
   * Server mode: fields whose value lists search on the backend. Typing in one
   * reports `{ [key]: query }` (one list is open at a time); closing it reports `{}`.
   */
  searchKeys?: readonly string[];
  onSearchChange?: (search: Record<string, string>) => void;
  /** Server mode: fields the backend listed only the top values of; their lists say so. */
  truncatedKeys?: readonly string[];
  /** How many values a truncated list shows, for its hint. */
  optionLimit?: number;
}

export const FacetedFilterBar = <T,>({
  fields,
  data,
  options,
  valueLabel,
  values,
  onChange,
  dateValue,
  onDateChange,
  dateLabel = "Date",
  searchKeys,
  onSearchChange,
  truncatedKeys,
  optionLimit,
}: FacetedFilterBarProps<T>) => {
  const [addOpen, setAddOpen] = useState(false);
  // Which field is being chosen/edited inside the add popover, and which chip is open.
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // A server search lasts as long as its value list is open: every list opens
  // and closes with an empty search box, so the options never stay narrowed by
  // a search that's no longer on screen.
  const clearSearch = useCallback(() => onSearchChange?.({}), [onSearchChange]);
  const openChip = (key: string | null) => {
    clearSearch();
    setEditingKey(key);
  };

  // Portaled date picker that opens when user clicks "Date" in the Add filter menu.
  const addBtnRef = useRef<HTMLDivElement>(null);
  const [addDateOpen, setAddDateOpen] = useState(false);
  const [addDatePos, setAddDatePos] = useState({ top: 0, left: 0 });
  // Local picker state so partial selections (start-only) don't reset mid-pick.
  const [addDatePickerRange, setAddDatePickerRange] = useState<CalendarDateRange | undefined>(undefined);

  // Distinct values per field — handed in by the server, or computed from the full dataset.
  const loadedOptions = useMemo(() => {
    if (options) return options;
    const map: Record<string, FacetOption[]> = {};
    fields.forEach((f) => {
      const seen = new Set<string>();
      (data ?? []).forEach((row) => {
        const v = f.get?.(row);
        if (v != null) seen.add(v);
      });
      map[f.key] = Array.from(seen)
        .map((value) => ({ value }))
        .sort((a, b) => a.value.localeCompare(b.value));
    });
    return map;
  }, [fields, data, options]);

  // A selected value can be missing from the options — they're still loading, or
  // another filter now excludes it. Keep it listed so it stays visible and can be
  // unchecked on its own.
  const optionsByField = useMemo(() => {
    const map: Record<string, FacetOption[]> = { ...loadedOptions };
    Object.entries(values).forEach(([key, selected]) => {
      const present = new Set((map[key] ?? []).map((o) => o.value));
      const missing = selected
        .filter((value) => !present.has(value))
        .map((value) => ({ value, label: valueLabel?.(key, value) ?? value }));
      if (missing.length) map[key] = [...missing, ...(map[key] ?? [])];
    });
    return map;
  }, [loadedOptions, values, valueLabel]);

  const searchProps = (key: string): Pick<ValueListProps, "onSearchChange" | "hint"> => ({
    onSearchChange: searchKeys?.includes(key) ? (query) => onSearchChange?.({ [key]: query }) : undefined,
    hint: truncatedKeys?.includes(key) ? `Showing top ${optionLimit ?? "values"}, type to search` : undefined,
  });

  const hasDateFilter = !!onDateChange;
  const dateActive = hasDateFilter && !!dateValue;

  const activeFields = fields.filter((f) => (values[f.key]?.length ?? 0) > 0);
  const inactiveFields = fields.filter((f) => (values[f.key]?.length ?? 0) === 0);
  const canAddMore = inactiveFields.length > 0 || (hasDateFilter && !dateActive);
  const anyActive = activeFields.length > 0 || dateActive;

  const toggleValue = useCallback(
    (key: string, value: string) => {
      const current = values[key] ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      const { [key]: _drop, ...rest } = values;
      onChange(next.length ? { ...rest, [key]: next } : rest);
    },
    [values, onChange],
  );

  const clearField = useCallback(
    (key: string) => {
      const { [key]: _drop, ...rest } = values;
      onChange(rest);
    },
    [values, onChange],
  );

  const closeAdd = useCallback(() => {
    clearSearch();
    setAddOpen(false);
    setAddingKey(null);
  }, [clearSearch]);

  const handleAddDate = useCallback(() => {
    closeAdd();
    setAddDatePickerRange(undefined); // start fresh for new selection
    if (addBtnRef.current) {
      const r = addBtnRef.current.getBoundingClientRect();
      setAddDatePos({ top: r.bottom + 8, left: r.left });
    }
    setAddDateOpen(true);
  }, [closeAdd]);

  const chipSummary = (field: FacetField): string => {
    const vals = values[field.key] ?? [];
    const first =
      optionsByField[field.key]?.find((o) => o.value === vals[0])?.label ?? valueLabel?.(field.key, vals[0]) ?? vals[0];
    if (vals.length === 1) return first || "—";
    return `${first || "—"} +${vals.length - 1}`;
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/* Date chip — shown when a date range is active */}
        {dateActive && (
          <DateChip
            range={dateValue!}
            label={dateLabel}
            onChange={(r) => onDateChange?.(r)}
            onClear={() => onDateChange?.(undefined)}
          />
        )}

        {/* Faceted field chips */}
        {activeFields.map((field) => (
          <Popover
            key={field.key}
            open={editingKey === field.key}
            onClose={() => openChip(null)}
            trigger={
              <div className="flex items-center overflow-hidden rounded-full border border-limestone-300 bg-white text-xs">
                <button
                  type="button"
                  onClick={() => openChip(editingKey === field.key ? null : field.key)}
                  className="flex items-center gap-1 py-1 pl-2.5 pr-1.5 hover:bg-limestone-50"
                >
                  <span className="font-medium text-text-secondary">{field.label}:</span>
                  <span className="max-w-[12rem] truncate text-text-primary">{chipSummary(field)}</span>
                  <ChevronDownIcon className="h-3 w-3 text-text-secondary" />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${field.label} filter`}
                  onClick={() => {
                    clearField(field.key);
                    openChip(null);
                  }}
                  className="border-l border-limestone-300 px-1.5 py-1 text-text-secondary hover:bg-limestone-50 hover:text-primary"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            }
          >
            <ValueList
              options={optionsByField[field.key] ?? []}
              selected={values[field.key] ?? []}
              onToggle={(v) => toggleValue(field.key, v)}
              onClear={() => clearField(field.key)}
              {...searchProps(field.key)}
            />
          </Popover>
        ))}

        {/* Add filter button */}
        <div ref={addBtnRef}>
          <Popover
            open={addOpen}
            onClose={closeAdd}
            trigger={
              <button
                type="button"
                onClick={() => (addOpen ? closeAdd() : setAddOpen(true))}
                disabled={!canAddMore && !addingKey}
                className={clsx(
                  "flex items-center gap-1 rounded-full border border-dashed px-2.5 py-1 text-xs transition-colors",
                  !canAddMore && !addingKey
                    ? "cursor-not-allowed border-limestone-200 text-limestone-300"
                    : "border-limestone-300 text-text-secondary hover:border-rio-500 hover:text-primary",
                )}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add filter
              </button>
            }
          >
            {addingKey ? (
              <ValueList
                options={optionsByField[addingKey] ?? []}
                selected={values[addingKey] ?? []}
                onToggle={(v) => toggleValue(addingKey, v)}
                onClear={() => clearField(addingKey)}
                {...searchProps(addingKey)}
              />
            ) : (
              <div className="flex flex-col">
                <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-text-secondary">
                  Filter by
                </p>
                {/* Date option — shown when date filter is enabled and not yet active */}
                {hasDateFilter && !dateActive && (
                  <button
                    type="button"
                    onClick={handleAddDate}
                    className="rounded-md px-2 py-1.5 text-left text-xs text-text-primary hover:bg-limestone-50"
                  >
                    {dateLabel}
                  </button>
                )}
                {inactiveFields.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => {
                      clearSearch();
                      setAddingKey(f.key);
                    }}
                    className="rounded-md px-2 py-1.5 text-left text-xs text-text-primary hover:bg-limestone-50"
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </Popover>
        </div>

        {anyActive && (
          <button
            type="button"
            onClick={() => {
              onChange({});
              onDateChange?.(undefined);
            }}
            className="flex items-center gap-1 text-xs text-text-secondary hover:text-primary"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
            Clear all
          </button>
        )}
      </div>

      {/* Portaled CalendarPicker — opened when user picks "Date" from the Add filter menu */}
      {addDateOpen &&
        createPortal(
          <>
            <BodyClass name="date-chip-open" />
            <div className="fixed inset-0 z-[200]" onClick={() => setAddDateOpen(false)} aria-hidden />
            <div
              className="fixed z-[201] rounded-lg border border-limestone-100 bg-white p-3 shadow-lg"
              style={{ top: addDatePos.top, left: addDatePos.left, minWidth: 300, animation: "aa-rise 140ms ease-out" }}
            >
              <CalendarPicker
                placeholder="Date"
                inputSize="full"
                mode="filter"
                selectedDateRange={addDatePickerRange}
                selectedLabel={addDatePickerRange?.selectedLabel}
                onDateChange={(r) => {
                  setAddDatePickerRange(r); // track partial selection
                  if (r.startDate && r.endDate) {
                    onDateChange?.(r);
                    setAddDateOpen(false);
                  }
                }}
              />
            </div>
          </>,
          document.body,
        )}
    </>
  );
};
